import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { parseLocalDateOnly } from "@/lib/dateUtils";

export interface CleanerPerformance {
  cleaner_id: string;
  cleaner_name: string;
  total_cleans_completed: number;
  avg_duration_minutes: number | null;
  avg_completion_percentage: number | null;
  total_issues: number;
  issue_rate_per_10: number | null;
  last_clean_date: string | null;
  tasks: CleanerTaskDetail[];
}

export interface CleanerTaskDetail {
  task_id: string;
  scheduled_date: string;
  completed_at: string | null;
  checklist_name: string | null;
  duration_minutes: number | null;
  completion_percentage: number | null;
  issue_count: number;
  submission_id: string | null;
  extra_dirty: boolean | null;
  extra_dirty_notes: string | null;
}

export interface PropertyPerformanceData {
  cleaners: CleanerPerformance[];
  propertyAverage: {
    avg_duration_minutes: number | null;
    avg_completion_percentage: number | null;
    issue_rate_per_10: number | null;
  };
}

export const useCleanerPerformance = (propertyId: string) => {
  return useQuery({
    queryKey: ["cleaner-performance", propertyId],
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    refetchOnMount: false, // Don't refetch if data exists in cache
    placeholderData: { cleaners: [], propertyAverage: null }, // Show empty data while loading
    queryFn: async () => {
      // Fetch tasks without embedded relations so owner RLS can read them (embedding cleaners/checklists can be blocked by RLS).
      const { data: tasks, error: tasksError } = await supabase
        .from("cleaning_tasks")
        .select("id, property_id, cleaner_id, status, scheduled_date, completed_at, duration_minutes, extra_dirty, extra_dirty_notes, checklist_id")
        .eq("property_id", propertyId)
        .not("cleaner_id", "is", null)
        .order("scheduled_date", { ascending: false });

      if (tasksError) {
        console.error("[useCleanerPerformance] Error fetching tasks:", tasksError);
        return {
          cleaners: [],
          propertyAverage: {
            avg_duration_minutes: null,
            avg_completion_percentage: null,
            issue_rate_per_10: null,
          },
        };
      }

      // Resolve cleaner names in a separate query (owners may not have SELECT on cleaners via embed)
      const cleanerIds = [...new Set((tasks || []).map((t: { cleaner_id: string }) => t.cleaner_id).filter(Boolean))];
      const cleanerNameMap = new Map<string, string>();
      if (cleanerIds.length > 0) {
        const { data: cleanersRows } = await supabase
          .from("cleaners")
          .select("id, name")
          .in("id", cleanerIds);
        for (const row of cleanersRows || []) {
          cleanerNameMap.set(row.id, row.name ?? "Cleaner");
        }
      }

      // Resolve checklist names in a separate query
      const checklistIds = [...new Set((tasks || []).map((t: { checklist_id?: string | null }) => t.checklist_id).filter(Boolean))] as string[];
      const checklistNameMap = new Map<string, string>();
      if (checklistIds.length > 0) {
        const { data: checklistRows } = await supabase
          .from("checklists")
          .select("id, name")
          .in("id", checklistIds);
        for (const row of checklistRows || []) {
          checklistNameMap.set(row.id, row.name ?? "");
        }
      }

      // Get all submissions for this property to link with tasks
      const { data: submissions, error: submissionsError } = await supabase
        .from("cleaning_submissions")
        .select("id, cleaning_task_id, cleaner_id, completion_percentage, issues")
        .eq("property_id", propertyId);

      if (submissionsError && submissionsError.code !== "400") {
        console.error("[useCleanerPerformance] Error fetching submissions:", submissionsError);
      }

      const submissionMap = new Map<string, { id: string; completion_percentage: number | null; issue_count: number }>();
      for (const sub of submissions || []) {
        if (sub.cleaning_task_id) {
          const issues = Array.isArray(sub.issues) ? sub.issues : [];
          submissionMap.set(sub.cleaning_task_id, {
            id: sub.id,
            completion_percentage: sub.completion_percentage,
            issue_count: issues.length,
          });
        }
      }

      // Group tasks by cleaner (no longer skip when cleaners embed was null)
      const cleanerMap = new Map<string, CleanerPerformance>();

      for (const task of tasks || []) {
        if (!task.cleaner_id) continue;

        const cleanerId = task.cleaner_id;
        const cleanerName = cleanerNameMap.get(cleanerId) ?? "Cleaner";
        const checklistName = task.checklist_id ? checklistNameMap.get(task.checklist_id) ?? null : null;
        const submission = submissionMap.get(task.id);

        if (!cleanerMap.has(cleanerId)) {
          cleanerMap.set(cleanerId, {
            cleaner_id: cleanerId,
            cleaner_name: cleanerName,
            total_cleans_completed: 0,
            avg_duration_minutes: null,
            avg_completion_percentage: null,
            total_issues: 0,
            issue_rate_per_10: null,
            last_clean_date: null,
            tasks: [],
          });
        }

        const cleaner = cleanerMap.get(cleanerId)!;

        if (task.status === "completed") {
          cleaner.total_cleans_completed++;
          cleaner.total_issues += submission?.issue_count || 0;
          if (task.completed_at) {
            if (!cleaner.last_clean_date || task.completed_at > cleaner.last_clean_date) {
              cleaner.last_clean_date = task.completed_at;
            }
          }
        }

        cleaner.tasks.push({
          task_id: task.id,
          scheduled_date: task.scheduled_date,
          completed_at: task.completed_at,
          checklist_name: checklistName ?? null,
          duration_minutes: task.duration_minutes,
          completion_percentage: submission?.completion_percentage || null,
          issue_count: submission?.issue_count || 0,
          submission_id: submission?.id || null,
          extra_dirty: task.extra_dirty,
          extra_dirty_notes: task.extra_dirty_notes,
        });
      }

      // Calculate averages and issue rate
      for (const cleaner of cleanerMap.values()) {
        const completedTasks = cleaner.tasks.filter(t => t.completed_at !== null);
        
        // Average duration (only completed tasks with duration, EXCLUDE extra dirty tasks)
        const tasksWithDuration = completedTasks.filter(t => t.duration_minutes !== null && !t.extra_dirty);
        if (tasksWithDuration.length > 0) {
          const totalDuration = tasksWithDuration.reduce((sum, t) => sum + (t.duration_minutes || 0), 0);
          cleaner.avg_duration_minutes = Math.round(totalDuration / tasksWithDuration.length);
        }

        // Average completion percentage
        const tasksWithCompletion = cleaner.tasks.filter(t => t.completion_percentage !== null);
        if (tasksWithCompletion.length > 0) {
          const totalCompletion = tasksWithCompletion.reduce((sum, t) => sum + (t.completion_percentage || 0), 0);
          cleaner.avg_completion_percentage = Math.round(totalCompletion / tasksWithCompletion.length);
        }

        // Issue rate per 10 cleans
        if (cleaner.total_cleans_completed > 0) {
          cleaner.issue_rate_per_10 = Math.round((cleaner.total_issues / cleaner.total_cleans_completed) * 10 * 10) / 10;
        }

        // Sort tasks by date descending
        cleaner.tasks.sort((a, b) => 
          parseLocalDateOnly(b.scheduled_date).getTime() - parseLocalDateOnly(a.scheduled_date).getTime()
        );
      }

      // Calculate property-level averages
      const allCleaners = Array.from(cleanerMap.values());
      
      // Property average duration
      const cleanersWithDuration = allCleaners.filter(c => c.avg_duration_minutes !== null);
      const propertyAvgDuration = cleanersWithDuration.length > 0
        ? Math.round(cleanersWithDuration.reduce((sum, c) => sum + (c.avg_duration_minutes || 0), 0) / cleanersWithDuration.length)
        : null;

      // Property average completion
      const cleanersWithCompletion = allCleaners.filter(c => c.avg_completion_percentage !== null);
      const propertyAvgCompletion = cleanersWithCompletion.length > 0
        ? Math.round(cleanersWithCompletion.reduce((sum, c) => sum + (c.avg_completion_percentage || 0), 0) / cleanersWithCompletion.length)
        : null;

      // Property average issue rate
      const totalPropertyIssues = allCleaners.reduce((sum, c) => sum + c.total_issues, 0);
      const totalPropertyCleans = allCleaners.reduce((sum, c) => sum + c.total_cleans_completed, 0);
      const propertyIssueRate = totalPropertyCleans > 0
        ? Math.round((totalPropertyIssues / totalPropertyCleans) * 10 * 10) / 10
        : null;

      // Sort cleaners by total cleans completed (desc)
      const sortedCleaners = allCleaners.sort((a, b) => 
        b.total_cleans_completed - a.total_cleans_completed
      );

      return {
        cleaners: sortedCleaners,
        propertyAverage: {
          avg_duration_minutes: propertyAvgDuration,
          avg_completion_percentage: propertyAvgCompletion,
          issue_rate_per_10: propertyIssueRate,
        },
      } as PropertyPerformanceData;
    },
    enabled: !!propertyId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};